// @ts-nocheck
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Bell, Check } from 'lucide-react';

interface NotificationSignupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tournamentId: string;
  tournamentName: string;
}

export default function NotificationSignupModal({
  open,
  onOpenChange,
  tournamentId,
  tournamentName,
}: NotificationSignupModalProps) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const validatePhoneNumber = (phone: string) => {
    // Basic phone validation - removes all non-digits and checks length
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10 && cleaned.length <= 15;
  };

  const formatPhoneNumber = (phone: string) => {
    // Remove all non-digits
    const cleaned = phone.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX for US numbers
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    // For international numbers, just add + prefix
    if (cleaned.length > 10) {
      return `+${cleaned}`;
    }
    
    return cleaned;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePhoneNumber(phoneNumber)) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid phone number with at least 10 digits.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      
      const { error } = await supabase
        .from('tournament_notifications')
        .insert({
          tournament_id: tournamentId,
          phone_number: formattedPhone,
          notification_type: 'sms',
        });

      if (error) throw error;

      setIsSuccess(true);
      toast({
        title: 'Successfully Signed Up!',
        description: `You'll receive SMS updates for ${tournamentName}.`,
      });

      // Reset form after 2 seconds and close modal
      setTimeout(() => {
        setPhoneNumber('');
        setIsSuccess(false);
        onOpenChange(false);
      }, 2000);
    } catch (error) {
      console.error('Error signing up for notifications:', error);
      toast({
        title: 'Signup Failed',
        description: 'There was an error signing up for notifications. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only digits, spaces, parentheses, hyphens, and plus sign
    const cleaned = value.replace(/[^0-9\s\(\)\-\+]/g, '');
    setPhoneNumber(cleaned);
  };

  if (isSuccess) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">You're All Set!</h3>
            <p className="text-muted-foreground">
              You'll receive SMS updates for {tournamentName} at {formatPhoneNumber(phoneNumber)}.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Get Tournament Updates
          </DialogTitle>
          <DialogDescription>
            Enter your phone number to receive SMS notifications about {tournamentName} including 
            schedule updates, score updates, and important announcements.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={phoneNumber}
              onChange={handlePhoneChange}
              required
              disabled={isLoading}
              className="text-base"
            />
            <p className="text-xs text-muted-foreground">
              Standard SMS rates may apply. You can unsubscribe at any time.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !phoneNumber.trim()}
              className="flex-1"
            >
              {isLoading ? 'Signing Up...' : 'Sign Up'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}